import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../lib/api';
import api from '../lib/api';

const AuthContext = createContext(null);

// Allowed parent origins for postMessage
const ALLOWED_ORIGINS = [
  'https://support.therapymgmt.com',
  'http://support.therapymgmt.com',
];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adminRoles, setAdminRoles] = useState([]);

  // Fetch admin roles for the authed user (best-effort)
  const refreshAdminRoles = useCallback(async () => {
    const token = localStorage.getItem('tactical_token');
    if (!token) { setAdminRoles([]); return; }
    try {
      const res = await api.get('/admin-roles/me');
      setAdminRoles(res.data?.roles || []);
    } catch (err) {
      setAdminRoles([]);
    }
    // Only reads localStorage + stable api ref; no captured state needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh roles whenever user becomes authenticated
  useEffect(() => {
    if (user) {
      refreshAdminRoles();
    } else {
      setAdminRoles([]);
    }
  }, [user, refreshAdminRoles]);

  // Check existing token in localStorage
  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('tactical_token');
    if (!token) {
      setLoading(false);
      return false;
    }

    try {
      const userData = await authApi.getMe();
      setUser(userData);
      setError(null);
      return true;
    } catch (err) {
      console.error('Auth check failed:', err);
      localStorage.removeItem('tactical_token');
      setUser(null);
      setError('Session expired. Please login again.');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Exchange Microsoft token for application JWT
  const exchangeToken = useCallback(async (msToken, email) => {
    try {
      console.log('Exchanging token for:', email);
      const response = await authApi.tokenExchange(msToken, email);
      
      if (response.token && response.user) {
        localStorage.setItem('tactical_token', response.token);
        setUser(response.user);
        setError(null);
        console.log('Token exchange successful for:', response.user.email);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Token exchange failed:', err);
      setError('Auto-login failed. Please try manual login.');
      return false;
    }
  }, []);

  // Handle URL parameters from the OAuth callback / parent iframe
  const handleUrlParams = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const msToken = params.get('token');
    const email = params.get('email');
    const errorParam = params.get('error');
    const loginCode = params.get('code');

    // Clean URL parameters for security — always BEFORE any network call, so a
    // refresh or back-navigation can never resubmit a sign-in credential.
    if (msToken || email || errorParam || loginCode) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (errorParam) {
      // If silent auth failed (login_required, interaction_required), just show login page
      if (errorParam === 'login_required' || errorParam === 'interaction_required') {
        return false;
      }
      
      // Provide actionable error messages for specific auth failures
      const errorMessages = {
        'invalid_client_secret': 'Login failed: The application client secret has expired or is invalid. Please contact your IT administrator to update the Entra ID client secret.',
        'expired_or_reused_code': 'Login failed: The authorization code has expired. Please try signing in again.',
        'redirect_uri_mismatch': 'Login failed: Redirect URI mismatch in Entra ID configuration. Please contact your IT administrator.',
        'token_exchange_failed': 'Login failed: Unable to complete authentication with Microsoft. Please try again or contact IT support.',
      };
      
      const friendlyMessage = errorMessages[errorParam] || `Login failed: ${errorParam}`;
      setError(friendlyMessage);
      return false;
    }

    // Preferred path: the Entra callback hands us a single-use opaque code,
    // which we swap for the real JWT over POST. The JWT never touches the URL.
    if (loginCode) {
      try {
        const response = await authApi.codeExchange(loginCode);
        if (response.token && response.user) {
          localStorage.setItem('tactical_token', response.token);
          return true;
        }
      } catch (err) {
        setError('Your sign-in link has expired. Please sign in again.');
        return false;
      }
    }

    // If we have both token and email from URL, exchange them (iframe flow)
    if (msToken && email) {
      console.log('Auto-login: token and email received from URL parameters');
      const success = await exchangeToken(msToken, email);
      return success;
    }

    // Legacy: application JWT handed over in the URL by an older backend.
    // Kept for ONE release so a user mid-login during the deploy is not
    // stranded; the backend no longer ever emits this. Safe to delete after.
    if (msToken && !email) {
      localStorage.setItem('tactical_token', msToken);
      return true; // Signal that we need to check auth
    }

    return false;
  }, [exchangeToken]);

  // Handle postMessage from parent iframe
  const handlePostMessage = useCallback(async (event) => {
    // Verify origin for security
    if (!ALLOWED_ORIGINS.includes(event.origin)) {
      console.log('Ignoring message from unauthorized origin:', event.origin);
      return;
    }

    if (event.data && event.data.type === 'AUTH_TOKEN') {
      const { token, email } = event.data;
      
      if (token && email) {
        console.log('Auto-login: token received via postMessage from', event.origin);
        const success = await exchangeToken(token, email);
        if (success) {
          setLoading(false);
        }
      }
    }
  }, [exchangeToken]);

  useEffect(() => {
    const initAuth = async () => {
      // DEV BYPASS — never auto-redirect to Microsoft when preview mode is on
      const devBypassOn = process.env.REACT_APP_DEV_BYPASS === 'true';

      // Step 1: Check URL parameters first (iframe authentication)
      const urlHandled = await handleUrlParams();
      
      if (urlHandled) {
        // If URL params resulted in successful token exchange, we're done
        // If it was a legacy JWT token, check auth
        const token = localStorage.getItem('tactical_token');
        if (token) {
          await checkAuth();
        }
        return;
      }

      // Step 2: Check existing stored token
      const storedToken = localStorage.getItem('tactical_token');
      if (storedToken) {
        await checkAuth();
        return;
      }

      // Step 3: Set up postMessage listener for iframe communication
      window.addEventListener('message', handlePostMessage);

      // In dev bypass mode, stop here — let the LoginPage render its dev panel
      if (devBypassOn) {
        setLoading(false);
        return;
      }

      // Step 4: Determine if we should auto-login
      const isInIframe = window.self !== window.top;
      
      // If in iframe, wait a bit for postMessage (parent might send token)
      if (isInIframe) {
        console.log('Running in iframe, waiting for auth message...');
        // Give parent iframe time to send token via postMessage
        setTimeout(() => {
          if (!localStorage.getItem('tactical_token')) {
            // Still no token after waiting, try silent SSO
            tryAutoLogin();
          }
        }, 1000);
        return;
      }

      // Skip auto-login for admin page (it has its own auth)
      if (window.location.pathname === '/admin') {
        setLoading(false);
        return;
      }

      // Not in iframe - try auto-login if user hasn't manually logged out
      if (!sessionStorage.getItem('manual_logout')) {
        tryAutoLogin();
      } else {
        setLoading(false);
      }
    };

    const tryAutoLogin = async () => {
      // Skip auto-login for admin page
      if (window.location.pathname === '/admin') {
        setLoading(false);
        return;
      }
      
      try {
        const data = await authApi.getLoginUrl();
        // Go directly to Microsoft login (not silent)
        window.location.href = data.auth_url;
      } catch (err) {
        console.error('Auto-login failed:', err);
        setLoading(false);
      }
    };

    initAuth();

    // Cleanup
    return () => {
      window.removeEventListener('message', handlePostMessage);
    };
  }, [checkAuth, handleUrlParams, handlePostMessage]);

  const login = async () => {
    try {
      sessionStorage.removeItem('manual_logout');
      const data = await authApi.getLoginUrl();
      window.location.href = data.auth_url;
    } catch (err) {
      console.error('Login failed:', err);
      setError('Failed to initiate login');
    }
  };

  const devLogin = async (email) => {
    try {
      sessionStorage.removeItem('manual_logout');
      const response = await authApi.devLogin(email);
      if (response.token && response.user) {
        localStorage.setItem('tactical_token', response.token);
        setUser(response.user);
        setError(null);
        return true;
      }
      return false;
    } catch (err) {
      console.error('Dev login failed:', err);
      const detail = err?.response?.data?.detail || 'Dev login failed';
      setError(detail);
      return false;
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('tactical_token');
      sessionStorage.setItem('manual_logout', 'true');
      setUser(null);
    }
  };

  const value = {
    user,
    loading,
    error,
    adminRoles,
    refreshAdminRoles,
    login,
    devLogin,
    logout,
    isAuthenticated: !!user,
    clearError: () => setError(null),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
